import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Utensils, Clock, Flame, ShoppingCart, Check, ChefHat, Apple, Beef, Egg, Fish, ArrowRight, Edit3 } from 'lucide-react';
import { setProgressForSlug } from '../../pages/GuidesHubPage';
import RelatedGuides from './RelatedGuides';

const ARTICLE_SLUG = 'high-protein-meal-prep-guide';

const NAV = [
  { id: 'why', label: 'Why Prep' },
  { id: 'meals', label: 'Meals' },
  { id: 'plan', label: 'Weekly Plan' },
  { id: 'grocery', label: 'Grocery List' },
  { id: 'tips', label: 'Tips' },
  { id: 'faq', label: 'FAQ' },
];

const MEALS = [
  {
    id: 'breakfast',
    name: 'Turkey Sausage Egg Cups',
    time: '25 min',
    servings: 12,
    protein: 18,
    calories: 145,
    category: 'Breakfast',
    color: '#f59e0b',
    ingredients: ['12 eggs', '200g turkey sausage', '1 cup spinach', '1/2 cup peppers', '1/4 cup cheese', 'Salt & pepper'],
    steps: ['Preheat oven to 375F. Grease a 12-cup muffin tin.', 'Cook turkey sausage, crumble into pieces.', 'Divide sausage, spinach, peppers into muffin cups.', 'Whisk eggs, pour evenly. Top with cheese.', 'Bake 20 min. Cool and refrigerate up to 5 days.'],
  },
  {
    id: 'lunch',
    name: 'Chicken & Quinoa Power Bowl',
    time: '35 min',
    servings: 5,
    protein: 42,
    calories: 480,
    category: 'Lunch',
    color: '#22c55e',
    ingredients: ['750g chicken breast', '2 cups quinoa', '2 cups broccoli', '1 cup sweet potato', 'Avocado oil', 'Lemon-tahini dressing'],
    steps: ['Cook quinoa per package directions.', 'Season chicken with paprika, garlic, salt. Grill or bake at 400F for 22 min.', 'Roast broccoli and cubed sweet potato at 425F for 20 min.', 'Divide quinoa, chicken, and veggies into 5 containers.', 'Add dressing on the side. Keeps 4-5 days.'],
  },
  {
    id: 'dinner',
    name: 'Salmon Teriyaki with Rice',
    time: '30 min',
    servings: 4,
    protein: 38,
    calories: 520,
    category: 'Dinner',
    color: '#E63946',
    ingredients: ['4x 170g salmon fillets', '2 cups jasmine rice', '2 cups edamame', 'Teriyaki sauce', '1 tbsp sesame seeds', 'Green onion'],
    steps: ['Cook rice per package directions.', 'Brush salmon with teriyaki. Bake at 400F for 14 min.', 'Steam edamame for 5 min.', 'Divide rice, salmon, and edamame into 4 containers.', 'Top with sesame seeds and green onion. Keeps 3-4 days.'],
  },
  {
    id: 'snack',
    name: 'Greek Yogurt Protein Parfait',
    time: '10 min',
    servings: 5,
    protein: 28,
    calories: 290,
    category: 'Snack',
    color: '#8b5cf6',
    ingredients: ['5 cups Greek yogurt (2%)', '1 scoop vanilla protein powder', '1 cup granola', '1 cup mixed berries', '2 tbsp honey', 'Cinnamon'],
    steps: ['Mix protein powder into yogurt until smooth.', 'Layer: yogurt, berries, granola in 5 mason jars.', 'Drizzle with honey and cinnamon.', 'Seal and refrigerate. Keeps 4-5 days.', 'Add granola fresh on day-of for crunch.'],
  },
];

const WEEKLY_PLAN = {
  'Sunday': { label: 'Prep Day', meals: ['Cook 5 days of chicken + quinoa bowls', 'Bake 12 egg cups', 'Prep 4 salmon + rice containers', 'Assemble 5 parfaits'], time: '2-3 hours' },
  'Monday': { label: 'Day 1', meals: ['Egg Cups (2)', 'Chicken Power Bowl', 'Salmon Teriyaki', 'Protein Parfait'], protein: 126 },
  'Tuesday': { label: 'Day 2', meals: ['Egg Cups (2)', 'Chicken Power Bowl', 'Salmon Teriyaki', 'Protein Parfait'], protein: 126 },
  'Wednesday': { label: 'Day 3', meals: ['Egg Cups (2)', 'Chicken Power Bowl', 'Salmon Teriyaki', 'Protein Parfait'], protein: 126 },
  'Thursday': { label: 'Day 4', meals: ['Egg Cups (2)', 'Chicken Power Bowl', 'Fresh cook or leftovers', 'Protein Parfait'], protein: 88 },
  'Friday': { label: 'Day 5', meals: ['Egg Cups (2)', 'Chicken Power Bowl', 'Fresh cook or eat out', 'Protein Parfait'], protein: 88 },
  'Saturday': { label: 'Flex Day', meals: ['Flexible — eat out, cook fresh, or use leftovers'], protein: null },
};

const GROCERY = [
  { section: 'Protein', items: ['Chicken breast (750g)', 'Salmon fillets (4x 170g)', 'Turkey sausage (200g)', 'Eggs (1 dozen)', 'Greek yogurt (5 cups)'], color: '#E63946' },
  { section: 'Grains', items: ['Quinoa (2 cups dry)', 'Jasmine rice (2 cups dry)', 'Granola (1 cup)'], color: '#f59e0b' },
  { section: 'Vegetables', items: ['Broccoli (2 cups)', 'Spinach (1 cup)', 'Bell peppers (1/2 cup)', 'Sweet potato (1 cup)', 'Edamame (2 cups)', 'Green onion'], color: '#22c55e' },
  { section: 'Pantry', items: ['Teriyaki sauce', 'Avocado oil', 'Lemon-tahini dressing', 'Vanilla protein powder', 'Sesame seeds', 'Honey', 'Paprika, garlic, salt, pepper'], color: '#3b82f6' },
  { section: 'Fruit', items: ['Mixed berries (1 cup)'], color: '#8b5cf6' },
];

const TIPS = [
  { title: 'Invest in Containers', desc: 'Glass meal prep containers (5-pack) with airtight lids. Microwave-safe, no plastic chemicals leaching into hot food.' },
  { title: 'Cook Proteins First', desc: 'Protein takes the longest. Start chicken/salmon first, then prep sides while they cook. Parallel processing saves 30+ minutes.' },
  { title: 'Season in Bulk', desc: 'Make a big batch of seasoning mix (paprika, garlic, salt, onion powder). Use the same mix across meals for consistency.' },
  { title: 'Freeze Week 2', desc: 'Prep 2 weeks at once. Freeze the second week. Thaw overnight in the fridge. This cuts your total prep time in half.' },
  { title: 'Fresh > Frozen Greens', desc: 'Frozen broccoli/spinach works fine for cooked meals. Save fresh greens for salads and toppings added day-of.' },
];

const FAQS = [
  { q: 'How long do prepped meals last?', a: 'Most cooked meals last 4-5 days refrigerated. Salmon/fish is best within 3-4 days. Egg cups freeze well for up to 3 months.' },
  { q: 'Can I swap proteins?', a: 'Absolutely. Ground turkey, lean beef, tofu, shrimp, or canned tuna all work. Match cooking times to the protein you choose.' },
  { q: 'How much protein do I need daily?', a: 'For active individuals: 0.8-1g per pound of body weight. For a 170lb person, that\'s 136-170g protein daily. This meal plan provides ~126g base.' },
  { q: 'What if I get bored of the same meals?', a: 'Rotate sauces and seasonings weekly. Same base (chicken + grain + veggie) but different flavor profiles: teriyaki, Mediterranean, Mexican, Thai.' },
];

export default function MealPrepArticle() {
  const [activeNav, setActiveNav] = useState('why');
  const [selectedMeal, setSelectedMeal] = useState(0);
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [openFaq, setOpenFaq] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.round((scrollTop / docHeight) * 100)) : 0;
      setProgressForSlug(ARTICLE_SLUG, pct);
      const sections = NAV.map(n => document.getElementById(n.id));
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].getBoundingClientRect().top <= 150) { setActiveNav(NAV[i].id); break; }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleCheck = (key) => setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }));
  const meal = MEALS[selectedMeal];
  const dayPlan = WEEKLY_PLAN[selectedDay];

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0a0a0a] text-zinc-300">
      <div className="sticky top-[60px] z-40 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          {NAV.map(n => (
            <button key={n.id} onClick={() => document.getElementById(n.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className={`px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap transition-all ${activeNav === n.id ? 'bg-[#22c55e] text-black font-bold' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`nav-${n.id}`}>{n.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8 pb-20">
        <div className="mb-12">
          <span className="text-[#22c55e] text-[10px] uppercase tracking-widest font-heading">Nutrition / Meal Prep</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mt-3 mb-4 leading-tight">High Protein Meal Prep</h1>
          <p className="text-zinc-400 text-base max-w-xl">4 recipes, 1 grocery list, 2-3 hours of prep, and 126g+ protein daily for the entire week.</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-zinc-600 font-mono"><span>8 min read</span><span>Interactive recipes & grocery list</span></div>
        </div>

        {/* Why */}
        <section id="why" className="mb-16 scroll-mt-28" data-testid="section-why">
          <h2 className="font-heading text-2xl text-white mb-4">Why Meal Prep Changes Everything</h2>
          <p className="text-zinc-400 leading-relaxed mb-6">Most people fail their nutrition goals not because they don't know what to eat, but because they don't have it ready when they're hungry. Meal prep eliminates the decision fatigue that leads to ordering takeout at 8pm.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { val: '126g+', label: 'Daily Protein', icon: Beef, color: '#E63946' },
              { val: '2-3 hrs', label: 'Sunday Prep', icon: Clock, color: '#f59e0b' },
              { val: '5 days', label: 'Of Meals Ready', icon: Utensils, color: '#22c55e' },
              { val: '$60-80', label: 'Weekly Cost', icon: ShoppingCart, color: '#3b82f6' },
            ].map(s => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="bg-zinc-900/60 border border-zinc-800 p-4 text-center" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
                  <Icon className="w-5 h-5 mx-auto mb-2" style={{ color: s.color }} />
                  <p className="text-white font-heading text-xl font-bold">{s.val}</p>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">{s.label}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Meals */}
        <section id="meals" className="mb-16 scroll-mt-28" data-testid="section-meals">
          <h2 className="font-heading text-2xl text-white mb-2">The 4 Recipes</h2>
          <p className="text-zinc-500 text-sm mb-6">Click a meal to see the full recipe with ingredients and steps.</p>

          <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-hide">
            {MEALS.map((m, i) => (
              <button key={m.id} onClick={() => setSelectedMeal(i)}
                className={`px-3 py-2 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap border transition-all ${selectedMeal === i ? 'border-zinc-600 bg-zinc-800 text-white' : 'border-zinc-800/50 text-zinc-600 hover:text-zinc-400'}`}
                data-testid={`meal-tab-${m.id}`}>{m.category}</button>
            ))}
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 p-5" data-testid="meal-detail">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-white font-heading text-lg">{meal.name}</p>
                <div className="flex gap-3 mt-1 text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {meal.time}</span>
                  <span>{meal.servings} servings</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-heading font-bold" style={{ color: meal.color }}>{meal.protein}g</p>
                <p className="text-zinc-600 text-[10px] uppercase">protein/serving</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-zinc-600 text-[10px] uppercase tracking-wider font-heading mb-2">Ingredients</p>
                <ul className="space-y-1">
                  {meal.ingredients.map((ing, i) => (
                    <li key={ing} className="text-sm text-zinc-400 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: meal.color }} /> {ing}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-zinc-600 text-[10px] uppercase tracking-wider font-heading mb-2">Steps</p>
                <ol className="space-y-2">
                  {meal.steps.map((step, i) => (
                    <li key={step} className="text-sm text-zinc-400 flex gap-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono bg-zinc-800 text-zinc-500 flex-shrink-0 mt-0.5">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="flex gap-3 mt-4 pt-3 border-t border-zinc-800 text-xs text-zinc-500">
              <span><strong className="text-zinc-300">{meal.calories}</strong> cal</span>
              <span><strong className="text-zinc-300">{meal.protein}g</strong> protein</span>
              <span><strong className="text-zinc-300">{meal.servings}</strong> servings</span>
            </div>
          </div>
        </section>

        {/* Weekly Plan */}
        <section id="plan" className="mb-16 scroll-mt-28" data-testid="section-plan">
          <h2 className="font-heading text-2xl text-white mb-2">Weekly Plan</h2>
          <p className="text-zinc-500 text-sm mb-6">Click a day to see what you eat.</p>

          <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-hide">
            {Object.keys(WEEKLY_PLAN).map(day => (
              <button key={day} onClick={() => setSelectedDay(day)}
                className={`px-3 py-2 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap border transition-all ${selectedDay === day ? 'border-zinc-600 bg-zinc-800 text-white' : 'border-zinc-800/50 text-zinc-600 hover:text-zinc-400'}`}
                data-testid={`day-${day.toLowerCase()}`}>{day.slice(0, 3)}</button>
            ))}
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 p-5" data-testid="plan-detail">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-white font-heading text-lg">{selectedDay}</p>
                <p className="text-zinc-500 text-xs">{dayPlan.label}</p>
              </div>
              {dayPlan.protein && <div className="text-right"><p className="text-[#22c55e] font-heading font-bold text-lg">{dayPlan.protein}g</p><p className="text-zinc-600 text-[10px] uppercase">total protein</p></div>}
              {dayPlan.time && <div className="text-right"><p className="text-[#f59e0b] font-heading font-bold text-lg">{dayPlan.time}</p><p className="text-zinc-600 text-[10px] uppercase">prep time</p></div>}
            </div>
            <ul className="space-y-1.5">
              {dayPlan.meals.map((m, i) => (
                <li key={m} className="flex items-center gap-2 text-sm text-zinc-400 bg-zinc-800/30 px-3 py-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" /> {m}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Grocery List */}
        <section id="grocery" className="mb-16 scroll-mt-28" data-testid="section-grocery">
          <h2 className="font-heading text-2xl text-white mb-2">Grocery Checklist</h2>
          <p className="text-zinc-500 text-sm mb-6">Tap items to check them off as you shop.</p>
          <div className="space-y-4">
            {GROCERY.map(g => (
              <div key={g.section}>
                <p className="text-xs font-heading uppercase tracking-widest mb-2" style={{ color: g.color }}>{g.section}</p>
                <div className="space-y-1">
                  {g.items.map(item => {
                    const key = `${g.section}-${item}`;
                    return (
                      <button key={item} onClick={() => toggleCheck(key)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left border transition-all ${checkedItems[key] ? 'border-zinc-800/30 bg-zinc-900/20 line-through text-zinc-700' : 'border-zinc-800/50 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700'}`}
                        data-testid={`grocery-${item.slice(0,15).toLowerCase().replace(/[^a-z0-9]/g,'-')}`}>
                        <span className={`w-4 h-4 border rounded-sm flex items-center justify-center flex-shrink-0 ${checkedItems[key] ? 'bg-[#22c55e] border-[#22c55e]' : 'border-zinc-700'}`}>
                          {checkedItems[key] && <Check className="w-3 h-3 text-black" />}
                        </span>
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section id="tips" className="mb-16 scroll-mt-28" data-testid="section-tips">
          <h2 className="font-heading text-2xl text-white mb-6">Pro Tips</h2>
          <div className="space-y-3">
            {TIPS.map((t, i) => (
              <div key={t.title} className="bg-zinc-900/40 border border-zinc-800/50 p-4" data-testid={`tip-${i}`}>
                <p className="text-white text-sm font-bold mb-1">{t.title}</p>
                <p className="text-zinc-500 text-xs leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-16 scroll-mt-28" data-testid="section-faq">
          <h2 className="font-heading text-2xl text-white mb-6">FAQ</h2>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <div key={f.q} className={`border transition-all ${openFaq === i ? 'border-zinc-700 bg-zinc-900/60' : 'border-zinc-800/50'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left" data-testid={`faq-${i}`}>
                  <span className="text-white text-sm font-bold pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
                </button>
                {openFaq === i && <p className="px-4 pb-4 text-zinc-400 text-sm leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        <RelatedGuides currentSlug={ARTICLE_SLUG} />

        {/* CTA to full Meal Planner tool */}
        <div className="my-10 border border-[#22c55e]/30 bg-[#22c55e]/5 p-6 text-center" data-testid="customize-plan-cta">
          <Edit3 className="w-6 h-6 text-[#22c55e] mx-auto mb-3" />
          <h3 className="font-heading text-xl text-white mb-2">Customize This Plan</h3>
          <p className="text-zinc-400 text-sm mb-4 max-w-md mx-auto">Swap recipes, adjust portions, add your own meals, and generate a personalized grocery list.</p>
          <Link to="/tools/meal-planner" className="inline-flex items-center gap-2 px-6 py-3 bg-[#22c55e] text-black text-xs font-heading uppercase tracking-wider hover:bg-[#22c55e]/80 transition-colors" data-testid="open-meal-planner-btn">
            Open Meal Planner <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="bg-amber-950/20 border border-amber-700/30 p-4 text-xs text-amber-200/70 leading-relaxed">
          <strong className="text-amber-200">Note:</strong> Nutritional values are approximate. Adjust portions based on your caloric needs and fitness goals. Consult a nutritionist for personalized meal planning.
        </div>
      </div>
    </div>
  );
}
